import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Loader2, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { tr } from "@/i18n/translator";

const NETWORKS = ["instagram", "facebook", "linkedin", "twitter", "tiktok"];

export default function LiveDemoWidget() {
  const [topic, setTopic] = useState("");
  const [network, setNetwork] = useState("instagram");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/demo/eligibility").then(({ data }) => setEligibility(data)).catch(() => {});
  }, []);

  const generate = async () => {
    if (!topic.trim() || topic.trim().length < 5) {
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/demo/generate", { topic, network });
      setResult(data);
      setEligibility({ eligible: false });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429) {
        setEligibility({ eligible: false, locked: true });
      } else {
        const msg = typeof detail === "string" ? detail : tr("Error al generar");
        alert(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="col-span-12 bg-[#0A0A0A] border border-[#FFD600] p-6 md:p-8 mt-6" data-testid="landing-live-demo">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <span className="font-mono-mark text-[10px] text-[#FFD600]">{tr("DEMO EN VIVO · GRATIS · 1 POR DISPOSITIVO")}</span>
        <span className="font-mono-mark text-[9px] bg-[#FFD600] text-[#0A0A0A] px-2 py-0.5">
          {tr("SIN REGISTRARTE")}
        </span>
      </div>

      <div className="font-display text-2xl md:text-3xl text-white mb-1">
        {tr("Pruébalo ahora — escribe un tema y mira la IA en vivo.")}
      </div>
      <p className="font-mono-data text-xs text-white/50 mb-5">
        {tr("Para descargar y usar el resultado: crea cuenta y elige un plan. Una demo por IP cada 24 h.")}
      </p>

      {!eligibility?.eligible && eligibility?.next_allowed_at ? (
        <div className="bg-[#1a1a1a] border border-white/20 p-5 text-white" data-testid="demo-locked">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-[#FFD600]" />
            <span className="font-mono-mark text-[10px] text-[#FFD600]">{tr("YA USASTE TU DEMO")}</span>
          </div>
          {eligibility?.previous && (
            <>
              <p className="text-sm whitespace-pre-wrap text-white/85 mb-3">{eligibility.previous.caption}</p>
              {eligibility.previous.hashtags?.length > 0 && (
                <div className="font-mono-data text-xs text-[#FFD600] mb-4">{eligibility.previous.hashtags.join(" ")}</div>
              )}
            </>
          )}
          <p className="font-mono-data text-xs text-white/60 mb-4">
            {tr("Para generar más, descargar este caption o abrir el generador completo, crea tu cuenta gratis.")}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/register")}
              className="bg-[#FFD600] text-[#0A0A0A] px-5 py-3 font-mono-mark text-[10px] border-2 border-[#FFD600] hover-brutal"
              data-testid="demo-cta-register"
            >
              {tr("CREAR CUENTA GRATIS")}
            </button>
            <button
              onClick={() => navigate("/login")}
              className="bg-transparent text-white px-5 py-3 font-mono-mark text-[10px] border-2 border-white/30 hover:border-white"
              data-testid="demo-cta-login"
            >
              {tr("YA TENGO CUENTA")}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-9">
              <input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={tr("Ej: Lanzamiento del nuevo café frío de oficina con descuento del 15%")}
                maxLength={300}
                disabled={loading}
                className="w-full bg-[#1a1a1a] border border-white/20 text-white placeholder:text-white/30 px-4 py-3 font-mono-data text-sm focus:border-[#FFD600] outline-none"
                data-testid="demo-topic"
              />
            </div>
            <div className="col-span-7 md:col-span-2">
              <select
                value={network}
                onChange={(e) => setNetwork(e.target.value)}
                disabled={loading}
                className="w-full bg-[#1a1a1a] border border-white/20 text-white px-3 py-3 font-mono-mark text-[10px] uppercase"
                data-testid="demo-network"
              >
                {NETWORKS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="col-span-5 md:col-span-1">
              <button
                onClick={generate}
                disabled={loading || topic.trim().length < 5}
                className="w-full h-full bg-[#FFD600] text-[#0A0A0A] px-3 py-3 font-mono-mark text-[10px] border border-[#FFD600] hover-brutal flex items-center justify-center gap-1 disabled:opacity-40"
                data-testid="demo-submit"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {loading ? "..." : "GO"}
              </button>
            </div>
          </div>

          {result && (
            <div className="mt-5 bg-[#1a1a1a] border border-[#FFD600] p-5 text-white" data-testid="demo-result">
              <div className="font-mono-mark text-[10px] text-[#FFD600] mb-2">{tr("RESULTADO · PREVIEW")} · {network.toUpperCase()}</div>
              <p className="text-sm whitespace-pre-wrap text-white/95">{result.caption}</p>
              {result.hashtags?.length > 0 && (
                <div className="font-mono-data text-xs text-[#FFD600] mt-3">{result.hashtags.join(" ")}</div>
              )}
              <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap gap-2 items-center">
                <span className="font-mono-data text-[11px] text-white/50 mr-2">{tr("¿Te gusta? Crea cuenta para descargar y agendar:")}</span>
                <button
                  onClick={() => navigate("/register")}
                  className="bg-[#FFD600] text-[#0A0A0A] px-4 py-2 font-mono-mark text-[10px] border-2 border-[#FFD600] hover-brutal"
                  data-testid="demo-result-register"
                >
                  {tr("CREAR CUENTA GRATIS")} →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
